import { Modal } from '@/components/Modals/modal';
import { NUTRITION, ORDER } from '@/constants';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ModalBody, ModalImpl } from '@/components/Modals/ModalFactory';
import useUser from '@/hooks/useUser';
import { motion } from 'framer-motion'; // <-- Added Framer Motion
import useLocalCart from '@/hooks/useLocalCart';

// Wrap the Link component
const MotionLink = motion(Link);

// 1. Extract navigation data for maintainability
interface MenuLink {
  id: string;
  label: string;
  path?: string;
  href?: string;
  isExternal?: boolean;
  condition?: () => string;
}

// ============================================================================
// FRAMER MOTION VARIANTS
// ============================================================================
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      type: 'spring',
      staggerChildren: 0.08, // Cascading stagger effect
      delayChildren: 0.1,
    },
  },
} as const;

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  show: {
    opacity: 1,
    x: 0,
    transition: { type: 'spring', stiffness: 300, damping: 24 },
  },
} as const;

export const NavigationMenuModal: ModalImpl = (props) => {
  return (
    <Modal
      variant="drawer-left"
      ariaLabel="Main Navigation Menu"
      className="!p-8 sm:!p-12 !overflow-visible" // Replaced static px with fluid Tailwind spacing
      {...props}
    >
      {NavigationMenu}
    </Modal>
  );
};

const NavigationMenu: ModalBody<null> = ({
  requestModalClose,
  withModalClose,
}) => {
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const NAV_LINKS: Array<MenuLink> = [
    { id: 'home', label: 'Home', path: '/' },
    {
      id: 'order',
      label: 'Order',
      path: `${ORDER}?step=menu`,
    },
    { id: 'nutrition', label: 'Nutrition', path: NUTRITION },
  ];

  const handleClose = () => {
    requestModalClose();
  };

  const handleSignIn = withModalClose(() => {
    const currentUrl = location.pathname + location.search;
    const encodedRedirect = encodeURIComponent(currentUrl);
    navigate(`/login?redirect=${encodedRedirect}`);
  });

  const handleSignOutClick = withModalClose(async () => {
    /**
     * @TODO - Add Loading Spinner
     */
    await logout();
    navigate('/', { replace: true });
  });

  return (
    <div className="flex flex-col justify-between h-full pt-4">
      <div>
        {/* ========================================== */}
        {/* HEADER AREA: User Profile or Sign In       */}
        {/* ========================================== */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="flex justify-start items-center"
        >
          {/* 2. Conditional User Profile / Sign In Block */}
          {user?.id ? (
            <div className="flex flex-col gap-y-1">
              <span className="font-display font-extrabold text-text-heading text-3xl sm:text-4xl tracking-tight">
                Hi, {user?.fullName?.split(' ')?.[0]}
              </span>
              {/* Local routing via React Router */}

              {/* <Link
                to="/account"
                onClick={handleClose}
                className="group w-fit rounded-md outline-none focus-visible:ring-2 focus-visible:ring-brand-truffle focus-visible:ring-offset-4 transition-all"
              >
                <p className="font-body font-medium text-base sm:text-lg text-text-muted group-hover:text-text-main group-hover:underline transition-colors">
                  View My Account
                </p>
              </Link> */}
            </div>
          ) : (
            <button
              onClick={() => handleSignIn()}
              className="group text-left rounded-md outline-none focus-visible:ring-2 focus-visible:ring-brand-truffle focus-visible:ring-offset-4 transition-all hover:scale-110 focus-visible:scale-110 cursor-pointer"
              role="button"
              aria-label="Sign In"
            >
              <span className="font-display font-extrabold text-3xl sm:text-4xl text-text-heading group-hover:text-brand-truffle transition-colors tracking-tight">
                Sign in
              </span>
            </button>
          )}
        </motion.div>

        {/* Divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.2, ease: 'easeInOut' }}
          className="my-8 sm:my-10 border-b border-brand-truffle/10 origin-left"
        />

        {/* ========================================== */}
        {/* NAVIGATION LINKS                           */}
        {/* ========================================== */}
        <motion.nav
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-y-6 sm:gap-y-8 lg:gap-y-12"
        >
          {NAV_LINKS.map((link: MenuLink) => {
            // Determine active state for internal links
            const isActive =
              !link.isExternal && location.pathname === link.path;
            const baseLinkClasses = `
              group relative flex items-center w-fit rounded-lg outline-none 
              focus-visible:ring-2 focus-visible:ring-brand-truffle focus-visible:ring-offset-8 hover:scale-105 focus-visible:scale-105 
              transition-all duration-300
            `;

            const textClasses = `
              font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter transition-colors duration-300
              ${isActive ? 'text-brand-truffle' : 'text-text-main group-hover:text-brand-truffle/70'}
            `;

            const content = (
              <>
                <span className={textClasses}>{link.label}</span>
                {/* Modern subtle dot indicator for active page */}
                {isActive && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute -left-6 w-2.5 h-2.5 rounded-full bg-brand-sage animate-ping"
                  />
                )}
              </>
            );

            // External Anchor
            if (link.isExternal) {
              return (
                <motion.a
                  variants={itemVariants}
                  key={link.id}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={baseLinkClasses}
                  onClick={handleClose}
                >
                  {content}
                </motion.a>
              );
            }

            // Internal React Router Link
            return (
              <MotionLink
                variants={itemVariants}
                key={link.id}
                to={link?.condition ? link.condition() : link.path!}
                className={baseLinkClasses}
                onClick={handleClose}
              >
                {content}
              </MotionLink>
            );
          })}
        </motion.nav>
      </div>

      {/* ========================================== */}
      {/* FOOTER AREA: Sign Out                      */}
      {/* ========================================== */}
      <div className="flex basis-20 items-center justify-start pb-4">
        {user?.id && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            onClick={handleSignOutClick}
            className="group rounded-md outline-none focus-visible:ring-2 focus-visible:ring-brand-truffle focus-visible:ring-offset-4 transition-all cursor-pointer hover:scale-110 focus-visible:scale-110"
            aria-label="Sign Out"
          >
            <p className="font-body font-medium text-xl text-text-muted group-hover:text-red-700 transition-colors m-0">
              Sign out
            </p>
          </motion.button>
        )}
      </div>
    </div>
  );
};
